import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { AgentSkillsContent } from "components/Launchables/PhysicalAIPage";
import Azure from "assets/img/svg/clouds/azure.svg?react";

export type PipelineFaq = {
  question: string;
  answer: string;
  links?: readonly (readonly [string, string])[];
  linkItems?: readonly {
    label: string;
    href: string;
    description: string;
  }[];
  example?: string;
};
export const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3 className="text-white/85 text-sm font-semibold mt-6 mb-2 first:mt-0">
    {children}
  </h3>
);

export const Paragraph: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="text-white/85 text-sm leading-relaxed">{children}</p>;

export const InlineLink: React.FC<{
  href: string;
  children: React.ReactNode;
}> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="text-highlight hover:underline"
  >
    {children}
  </a>
);

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const LinkedText: React.FC<{
  text: string;
  links: readonly (readonly [string, string])[];
}> = ({ text, links }) => {
  const sortedLinks = [...links].sort((a, b) => b[0].length - a[0].length);
  const pattern = new RegExp(
    `(${sortedLinks.map(([label]) => escapeRegExp(label)).join("|")})`,
    "g",
  );

  return (
    <>
      {text.split(pattern).map((part, index) => {
        const href = sortedLinks.find(([label]) => label === part)?.[1];

        if (!href) {
          return (
            <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
          );
        }

        return (
          <InlineLink key={`${part}-${index}`} href={href}>
            {part}
          </InlineLink>
        );
      })}
    </>
  );
};

export const PromptExample: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <blockquote className="rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-sm italic leading-relaxed text-white/85">
    {children}
  </blockquote>
);

export const RequirementsTable: React.FC<{
  rows: readonly (readonly [React.ReactNode, React.ReactNode])[];
}> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm text-white/85 border border-zinc-800 rounded-md">
      <thead>
        <tr className="border-b border-zinc-800 text-left">
          <th className="px-3 py-2 font-medium">Resource</th>
          <th className="px-3 py-2 font-medium">Spec</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, v], index) => (
          <tr
            key={`${String(k)}-${index}`}
            className="border-b border-zinc-800 last:border-0"
          >
            <td className="px-3 py-2 text-white/85">{k}</td>
            <td className="px-3 py-2">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="bg-zinc-900 border border-zinc-700 rounded-md px-4 py-3 text-sm text-white/85 font-mono overflow-x-auto whitespace-pre-wrap">
    {children}
  </pre>
);

export const AgentDeployBanner: React.FC<{ skillFolderUrl?: string }> = ({
  skillFolderUrl,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className="bg-secondaryBg border border-zinc-800 rounded-md shadow-lg mb-6 overflow-hidden cursor-pointer hover:border-[#76b900] transition-colors"
      >
        <div className="bg-gradient-to-r from-[#76b900]/10 via-[#76b900]/5 to-transparent border-l-4 border-[#76b900]">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <h3 className="text-sm font-semibold text-primary">
                  Deploy on your own cloud{" "}
                  <span className="text-[#76b900]">→</span>
                </h3>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <button
                  aria-label="Deploy on Azure with your agent"
                  className="group flex flex-col items-center justify-center gap-1.5 w-24 py-2 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:border-[#76b900]/50 hover:bg-[#76b900]/5 transition-all duration-150"
                >
                  <Azure className="h-5" />
                  <span className="text-[10px] text-zinc-400 group-hover:text-zinc-300 transition-colors leading-none">
                    Microsoft Azure
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Launch on your cloud</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            <AgentSkillsContent skillFolderUrl={skillFolderUrl} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
